import { useState } from "react";
import axios from "axios";
import { Lock, ArrowRight, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import logo from "../assets/favicon.png";

const API = process.env.REACT_APP_API_URL;

// -------------------- LOGO --------------------

const Logo = () => (
  <div className="flex items-center justify-center gap-3 mb-8">
    <img src={logo} alt="Wol Dynamics" className="h-10 w-auto" />
    <span className="font-heading font-bold text-foreground text-xl leading-none">
      WOL <span className="text-primary">DYNAMICS</span>
    </span>
  </div>
);

// -------------------- LOGIN PAGE --------------------

export default function LoginPage() {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    if (!email.trim() || !password.trim()) {
      setError("Completá todos los campos.");
      return;
    }

    setLoading(true);
    try {
      // Llama al mismo endpoint /api/login que ya tenés en el backend.
      // El servidor valida las credenciales y devuelve un JWT.
      const { data } = await axios.post(`${API}/api/login`, { email, password });

      // Guardar el token — AdminPage lo lee con localStorage.getItem("access_token")
      localStorage.setItem("access_token", data.access_token);

      window.location.href = "/admin";
    } catch (err) {
      // Mensaje genérico para no dar pistas sobre qué campo falló
      if (err.response?.status === 429) {
        setError("Demasiados intentos. Esperá unos minutos.");
      } else {
        setError("Credenciales inválidas. Revisá tu email y contraseña.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030305] flex items-center justify-center px-4">
      {/* Background orbs */}
      <div className="orb orb-1 opacity-40" />
      <div className="orb orb-2 opacity-40" />

      <div className="relative z-10 w-full max-w-md">
        <Logo />

        <div className="glass-strong rounded-lg p-8 sm:p-10">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-6 h-6 text-primary" />
            </div>
            <h1 className="font-heading text-2xl font-semibold text-foreground">
              Panel de Administración
            </h1>
            <p className="text-muted-foreground text-sm mt-2">
              Ingresá tus credenciales para continuar
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="flex items-start gap-3 bg-destructive/10 border border-destructive/20 rounded-lg px-4 py-3 mb-6">
              <AlertCircle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-sm text-muted-foreground mb-2">
                Email
              </label>
              <Input
                type="email"
                placeholder="admin@woldynamics.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                autoComplete="email"
                className="bg-black/50 border-white/10 focus:border-primary/50 text-white h-12 disabled:opacity-50"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <label className="block text-sm text-muted-foreground mb-2">
                Contraseña
              </label>
              <Input
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                autoComplete="current-password"
                className="bg-black/50 border-white/10 focus:border-primary/50 text-white h-12 disabled:opacity-50"
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full skew-button bg-primary hover:bg-primary/90 text-black font-semibold h-12 text-base mt-2"
              data-testid="login-submit-button"
            >
              <span className="skew-button-content flex items-center justify-center gap-2">
                {loading ? "Ingresando..." : "Ingresar"}
                {!loading && <ArrowRight className="w-4 h-4" />}
              </span>
            </Button>
          </form>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          <a href="/" className="hover:text-primary transition-colors">
            ← Volver al sitio
          </a>
        </p>
      </div>
    </div>
  );
}